/**
 * Conocimiento — con qué reglas está contestando el agente.
 *
 * Esta pantalla era una fachada: listaba una entidad que nadie escribía y el
 * botón de cargar era un `alert()`. Lo que muestra ahora, y por qué:
 *
 * - **Reglas activas** cruza el catálogo del repo con el índice del RAG. De ese
 *   cruce sale `SIN_SEMBRAR`, que es el estado más útil: dice que alguien
 *   cambió un wording por PR y nadie volvió a correr la siembra. Mostrando
 *   solo el índice, la tabla se vería perfecta mientras el agente contesta con
 *   la versión anterior.
 * - **El wording completo, no un resumen.** Quien revisa esto tiene que poder
 *   leer la frase exacta que va a recibir el cliente.
 * - **El límite se muestra aparte y marcado**: es lo que el agente NO puede
 *   decir en ese caso. Confundirlo con el wording sería mandarle al cliente
 *   justo lo que ese campo prohíbe.
 */
import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import apiClient from '../api/client'
import { useAuth } from '../auth/useAuth'
import EmptyState from '../components/EmptyState'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'
import PageHeader from '../components/PageHeader'

interface Regla {
  case_id: string
  seccion: string
  titulo: string
  wording: string
  limite: string
  accion: string
  equipo: string
  origen: string
  estado: string
  activa: boolean
  aprobado: boolean
}

interface Resumen {
  catalogo: number
  activas: number
  pendientes_de_negocio: number
  sin_sembrar: number
  retiradas: number
  umbral: number
  top_k: number
  modelo: string
}

interface ReglasResponse {
  reglas: Regla[]
  count: number
  resumen: Resumen
}

interface KnowledgeDoc {
  id: string
  title: string
  type: string
  status: string
  chunks?: number
  origen?: string
  ingest_error?: string
  created_at: string
}

interface KnowledgeResponse {
  // `knowledge`, no `knowledges`: lo define _KEY_MAP en el backend.
  knowledge: KnowledgeDoc[]
  count: number
}

const STATUS_BADGE: Record<string, string> = {
  UPLOADED:         'badge-gray',
  PROCESSING:       'badge-blue',
  READY:            'badge-amber',
  PENDING_APPROVAL: 'badge-amber',
  PUBLISHED:        'badge-green',
  RETIRED:          'badge-gray',
  FAILED:           'badge-red',
  SIN_SEMBRAR:      'badge-red',
}

// Qué significa cada estado para quien mira la pantalla. `SIN_SEMBRAR` y
// `PENDING_APPROVAL` son los dos que se pueden confundir con "todo bien".
const STATUS_LABEL: Record<string, string> = {
  UPLOADED:         'Subido',
  PROCESSING:       'Procesando',
  READY:            'Listo, sin publicar',
  PENDING_APPROVAL: 'Falta aprobación de negocio',
  PUBLISHED:        'Activa',
  RETIRED:          'Retirada',
  FAILED:           'Falló',
  SIN_SEMBRAR:      'No está en el índice',
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`badge ${STATUS_BADGE[status] ?? 'badge-gray'}`}>
      {STATUS_LABEL[status] ?? status}
    </span>
  )
}

function Metric({ label, value, alerta }: { label: string; value: number | string; alerta?: boolean }) {
  return (
    <div className="card-panel px-4 py-3">
      <div className={`text-xl font-extrabold ${alerta ? 'text-danger' : 'text-ink'}`}>{value}</div>
      <div className="text-xs text-ink-muted">{label}</div>
    </div>
  )
}

export default function Knowledge() {
  const { isAdmin } = useAuth()
  const [vista, setVista] = useState<'reglas' | 'documentos'>('reglas')

  const [reglas, setReglas] = useState<Regla[]>([])
  const [resumen, setResumen] = useState<Resumen | null>(null)
  const [documentos, setDocumentos] = useState<KnowledgeDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [filtro, setFiltro] = useState('')
  const [abierta, setAbierta] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [r, d] = await Promise.all([
        apiClient.get<ReglasResponse>('/knowledge/rules'),
        apiClient.get<KnowledgeResponse>('/knowledge?limit=50'),
      ])
      setReglas(r.data.reglas ?? [])
      setResumen(r.data.resumen ?? null)
      setDocumentos(d.data.knowledge ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar el conocimiento')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void cargar() }, [cargar])

  const cambiarEstado = async (docId: string, accion: 'publish' | 'retire') => {
    setActionLoading(docId + accion)
    setActionError(null)
    try {
      await apiClient.post(`/knowledge/${docId}/${accion}`)
      await cargar()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al procesar la acción')
    } finally {
      setActionLoading(null)
    }
  }

  const subir = async (file: File) => {
    setActionLoading('upload')
    setActionError(null)
    try {
      const { data } = await apiClient.post<{ upload_url: string; content_type: string }>(
        '/knowledge',
        { title: file.name.replace(/\.[^.]+$/, ''), filename: file.name },
      )
      // PUT directo a S3: sin `apiClient`, porque su interceptor agrega el
      // Bearer de Cognito y la URL prefirmada ya lleva su propia firma. El
      // Content-Type tiene que ser exactamente el que se firmó o S3 rechaza.
      await axios.put(data.upload_url, file, {
        headers: { 'Content-Type': data.content_type },
      })
      await cargar()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'No se pudo subir el archivo')
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) return <LoadingSpinner label="Cargando conocimiento..." />
  if (error)   return <ErrorAlert message={error} onRetry={cargar} />

  const visibles = filtro
    ? reglas.filter((r) =>
        [r.case_id, r.seccion, r.titulo, r.wording].join(' ').toLowerCase()
          .includes(filtro.toLowerCase()))
    : reglas

  return (
    <div className="space-y-4">
      <PageHeader
        actions={
          isAdmin ? (
            <label className="btn-primary cursor-pointer">
              {actionLoading === 'upload' ? 'Subiendo…' : '+ Cargar documento'}
              <input
                type="file"
                accept=".txt,.md,.markdown"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) void subir(f)
                  e.target.value = ''
                }}
              />
            </label>
          ) : undefined
        }
      />

      {actionError && <ErrorAlert message={actionError} />}

      {resumen && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Metric label="Reglas del catálogo" value={resumen.catalogo} />
          <Metric label="Activas en el índice" value={resumen.activas} />
          <Metric
            label="Sin sembrar"
            value={resumen.sin_sembrar}
            alerta={resumen.sin_sembrar > 0}
          />
          <Metric
            label="Falta aprobación"
            value={resumen.pendientes_de_negocio}
            alerta={resumen.pendientes_de_negocio > 0}
          />
          <Metric label="Fragmentos por respuesta" value={resumen.top_k} />
        </div>
      )}

      {resumen && resumen.sin_sembrar > 0 && (
        <div className="card-panel border-warn-border bg-warn-light px-4 py-3 text-sm text-ink">
          <strong>{resumen.sin_sembrar} reglas del catálogo no están en el índice.</strong>{' '}
          El agente no las va a usar para responder una pregunta fuera de turno:
          hay que correr <code className="mono">scripts/sembrar_conocimiento.py</code> en
          este ambiente.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setVista('reglas')}
          className={`rounded-input border px-3 py-1.5 text-sm font-medium ${
            vista === 'reglas'
              ? 'border-brand bg-brand text-white'
              : 'border-surface-border bg-white text-ink-muted'
          }`}
        >
          Reglas activas ({reglas.length})
        </button>
        <button
          onClick={() => setVista('documentos')}
          className={`rounded-input border px-3 py-1.5 text-sm font-medium ${
            vista === 'documentos'
              ? 'border-brand bg-brand text-white'
              : 'border-surface-border bg-white text-ink-muted'
          }`}
        >
          Documentos ({documentos.length})
        </button>
        {vista === 'reglas' && (
          <input
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar por sección, caso o texto…"
            className="input ml-auto w-full sm:w-72"
          />
        )}
      </div>

      {vista === 'reglas' ? (
        visibles.length === 0 ? (
          <EmptyState
            icon="📚"
            title="Sin reglas"
            description="El catálogo de wording no devolvió ninguna regla para este filtro."
          />
        ) : (
          <div className="card overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Sección</th>
                  <th>Caso</th>
                  <th>Wording al cliente</th>
                  <th>Acción</th>
                  <th>Origen</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((r) => (
                  <tr
                    key={r.case_id}
                    onClick={() => setAbierta(abierta === r.case_id ? null : r.case_id)}
                    className="cursor-pointer"
                  >
                    <td className="mono whitespace-nowrap text-ink-muted">§{r.seccion}</td>
                    <td className="font-medium">
                      {r.titulo}
                      <div className="mono text-xs text-ink-faint">{r.case_id}</div>
                    </td>
                    <td className="max-w-md">
                      <div className={abierta === r.case_id ? '' : 'line-clamp-2'}>
                        {r.wording}
                      </div>
                      {abierta === r.case_id && r.limite && (
                        <div className="mt-2 rounded-input border border-warn-border bg-warn-light px-2 py-1 text-xs">
                          <strong>Límite (lo que el agente NO puede decir):</strong>{' '}
                          {r.limite}
                        </div>
                      )}
                      {!r.aprobado && (
                        <div className="mt-1 text-xs text-danger">
                          Wording provisional: negocio no lo confirmó, así que no
                          entra al índice.
                        </div>
                      )}
                    </td>
                    <td className="mono whitespace-nowrap text-xs text-ink-muted">{r.accion}</td>
                    <td className="text-ink-muted">{r.origen}</td>
                    <td><StatusBadge status={r.estado} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : documentos.length === 0 ? (
        <EmptyState
          icon="📄"
          title="Sin documentos subidos"
          description="Las reglas del PDD se versionan en el repo. Acá van los documentos que suba el equipo: texto (.txt o .md), que se trocea y se indexa al subirlo."
        />
      ) : (
        <div className="card overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>Título</th>
                <th>Tipo</th>
                <th>Fragmentos</th>
                <th>Estado</th>
                <th>Fecha</th>
                {isAdmin && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {documentos.map((doc) => (
                <tr key={doc.id}>
                  <td className="font-medium">
                    {doc.title}
                    {doc.ingest_error && (
                      <div className="text-xs text-danger">{doc.ingest_error}</div>
                    )}
                  </td>
                  <td className="text-ink-muted">{doc.type}</td>
                  <td className="mono text-ink-muted">{doc.chunks ?? 0}</td>
                  <td><StatusBadge status={doc.status} /></td>
                  <td className="mono whitespace-nowrap text-ink-muted">
                    {new Date(doc.created_at).toLocaleDateString('es-PE')}
                  </td>
                  {isAdmin && (
                    <td>
                      <div className="flex gap-1.5">
                        {(doc.status === 'READY' || doc.status === 'RETIRED') && (
                          <button
                            onClick={() => void cambiarEstado(doc.id, 'publish')}
                            disabled={actionLoading === doc.id + 'publish'}
                            className="rounded-input border border-brand-light bg-brand-light px-2.5 py-1 text-xs font-medium text-brand transition-colors hover:bg-brand hover:text-white disabled:opacity-50"
                          >
                            {actionLoading === doc.id + 'publish' ? '…' : 'Publicar'}
                          </button>
                        )}
                        {doc.status === 'PUBLISHED' && (
                          <button
                            onClick={() => void cambiarEstado(doc.id, 'retire')}
                            disabled={actionLoading === doc.id + 'retire'}
                            className="rounded-input border border-surface-border bg-surface-subtle px-2.5 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-surface-border disabled:opacity-50"
                          >
                            {actionLoading === doc.id + 'retire' ? '…' : 'Retirar'}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {resumen && (
        <p className="text-xs text-ink-faint">
          El agente responde una pregunta fuera de turno solo si encuentra una
          regla activa con similitud ≥ {resumen.umbral}; por debajo de ese umbral
          deriva a un ejecutivo en vez de improvisar. Embeddings:{' '}
          <span className="mono">{resumen.modelo}</span>.
        </p>
      )}
    </div>
  )
}
